import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import React, { useMemo, useState, useEffect } from 'react';

import {
  Box,
  Link,
  Button,
  Tooltip,
  TextField,
  Typography,
  IconButton,
  Autocomplete,
} from '@mui/material';

import { useToast } from 'src/hooks/useToast';
import { useModal } from 'src/hooks/useModal';
import { useFormContext } from 'src/hooks/useForm';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { genericApiToaster } from 'src/services/utils';
import { fetchTagsByDepartment } from 'src/services/tag.service';

import Iconify from 'src/components/iconify';
import TextInput from 'src/components/FormInputs/TextInput';

import AddUpdateTags from 'src/sections/tags/modals/AddUpdateTags';

import { boxStyle } from './style';

function Tags({ selectedDepartment, section, errorsTags, setErrorsTags }) {
  const { modifySection } = useFormContext();
  const { isSmallScreen } = useResponsiveScreen();
  const { t } = useTranslation();
  const { showToast, hideToast } = useToast();
  const { showModal, hideModal } = useModal();
  const [tags, setTags] = useState(section?.tags || []);
  const [availableTags, setAvailableTags] = useState([]);

  const handleCreateTagClick = (event, tagId, typedName) => {
    event.preventDefault();
    showModal({
      title: t('pages.tagsPage.addNewTag'),
      Component: AddUpdateTags,
      props: {
        hideModal,
        initialData: { name: typedName, departmentId: selectedDepartment },
        handleFetchTags: (newTag) => {
          if (newTag) {
            setAvailableTags((prev) => [...prev, newTag]);
            handleTagSelection(tagId, 'name', newTag.name);
            setErrorsTags((prevErrors) => {
              const newErrors = { ...prevErrors };
              if (newErrors[tagId]) {
                delete newErrors[tagId].name;
                if (Object.keys(newErrors[tagId]).length === 0) {
                  delete newErrors[tagId];
                }
              }
              return newErrors;
            });
          }
        },
      },
    });
  };

  const handleTagSelection = (id, key, value) => {
    const updatedTags = tags.map((tag) =>
      tag.id === id ? { ...tag, [key]: value } : tag
    );
    setTags(updatedTags);
    modifySection(section.id, { ...section, tags: updatedTags });
  };

  // Helper to instantly check if a typed tag is unregistered (bypasses prop lag)
  const isUnregistered = (tagName) => {
    if (!tagName || !tagName.trim()) return false;
    return !availableTags.some(
      (t) => t.name.toLowerCase() === tagName.toLowerCase().trim()
    );
  };

  const handleAddTag = () => {
    const newTag = { id: Date.now(), name: '', value: '' };
    setErrorsTags((prevErrors) => ({
      ...prevErrors,
      [newTag.id]: {
        name: t('formControl.fieldRequired'),
        value: t('formControl.fieldRequired'),
      },
    }));
    setTags([...tags, newTag]);
    modifySection(section.id, { ...section, tags: [...tags, newTag] });
  };

  const handleRemoveTag = (id) => {
    const updatedTags = tags.filter((tag) => tag.id !== id);
    setErrorsTags((prevErrors) => {
      const newErrors = { ...prevErrors };
      delete newErrors[id];
      return newErrors;
    });
    setTags(updatedTags);
    modifySection(section.id, { ...section, tags: updatedTags });
  };

  const handleFetchTagsByDepartment = async () => {
    genericApiToaster(
      fetchTagsByDepartment,
      [selectedDepartment],
      '',
      t('errorMessages.failedToFetchTags'),
      showToast,
      hideToast,
      t,
      setAvailableTags,
      undefined
    );
  };

  useEffect(() => {
    handleFetchTagsByDepartment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDepartment]);

  const handleTagChange = (tagId, key, newValue) => {
    setErrorsTags((prevErrors) => {
      const newErrors = { ...prevErrors };

      // Ensure the tag's error object exists
      if (!newErrors[tagId]) {
        newErrors[tagId] = {};
      }

      if (key === 'name') {
        const isDuplicate = tags.some((tag) => tag.name === newValue && tag.id !== tagId);
        const cleanVal = newValue?.trim() || '';
        const exists = availableTags.some(
          (t) => t.name.toLowerCase() === cleanVal.toLowerCase()
        );

        if (isDuplicate) {
          newErrors[tagId].name = t('formControl.duplicateTag');
        } else if (!cleanVal) {
          newErrors[tagId].name = t('formControl.fieldRequired');
        } else if (!exists) {
          newErrors[tagId].name = t('formControl.mustCreateAttribute');
        } else {
          delete newErrors[tagId].name;
        }
      }

      if (key === 'value') {
        if (!newValue?.trim()) {
          newErrors[tagId].value = t('formControl.fieldRequired');
        } else {
          delete newErrors[tagId].value;
        }
      }

      // If both name and value errors are cleared, remove the tag's error object
      if (Object.keys(newErrors[tagId]).length === 0) {
        delete newErrors[tagId];
      }

      return newErrors;
    });

    handleTagSelection(tagId, key, newValue);
  };

  const filteredAvailableTags = useMemo(
    () => availableTags.filter((tag) => !tags.some((t) => t.name === tag.name)),
    [availableTags, tags]
  );

  return (
    <>
      <Typography
        variant="subtitle2"
        sx={{ fontSize: isSmallScreen ? '0.675rem' : '1rem', mt: '2vh' }}
      >
        {t('attributes.tags')}
      </Typography>
      <Box spacing={2}>
        {tags.map((tag, index) => (
          <Box key={tag.id} sx={boxStyle}>
            {/* Tag Autocomplete Input (Wrapped in Box to support the inline creation Link) */}
            <Box sx={{ width: '45%', display: 'flex', flexDirection: 'column' }}>
              <Autocomplete
                freeSolo
                options={filteredAvailableTags.map((availableTag) => availableTag.name)}
                getOptionLabel={(option) => option.name || option}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('attributes.tag')}
                    variant="outlined"
                    error={!!errorsTags[tag.id]?.name}
                    helperText={errorsTags[tag.id]?.name}
                  />
                )}
                value={tag.name}
                onChange={(event, newValue) => handleTagChange(tag.id, 'name', newValue)}
                onInputChange={(event, newValue, reason) => {
                  if (reason === 'input') {
                    handleTagChange(tag.id, 'name', newValue);
                  }
                }}
                noOptionsText={null}
              />
              {isUnregistered(tag.name) && (
                <Link
                  component="button"
                  variant="caption"
                  onMouseDown={(e) => handleCreateTagClick(e, tag.id, tag.name)}
                  sx={{
                    fontWeight: 800,
                    textDecoration: 'none',
                    color: '#00A76F',
                    textAlign: 'left',
                    mt: 0.5,
                  }}
                >
                  {`+ ${t('buttons.add')} "${tag.name}"`}
                </Link>
              )}
            </Box>

            {/* Tag Value Input */}
            <TextInput
              defaultValue={tag.value}
              fullWidth
              label={t('attributes.value')}
              sx={{ width: '45%' }}
              onChange={(e) => handleTagChange(tag.id, 'value', e.target.value)}
              onBlur={(e) => handleTagChange(tag.id, 'value', e.target.value)}
              error={!!errorsTags[tag.id]?.value}
              helperText={errorsTags[tag.id]?.value}
            />

            {/* Delete Icon */}
            <Tooltip title={t('buttons.delete')}>
              <IconButton onClick={() => handleRemoveTag(tag.id)}>
                <Iconify icon="eva:trash-2-fill" />
              </IconButton>
            </Tooltip>
          </Box>
        ))}
      </Box>

      {/* Add Tag Button */}
      <Box sx={{ mt: 2, pl: 2 }}>
        <Button variant="contained" onClick={handleAddTag}>
          <Iconify icon="typcn:plus" sx={{ mr: 1 }} />
          {t('buttons.addTag')}
        </Button>
      </Box>
    </>
  );
}

Tags.propTypes = {
  selectedDepartment: PropTypes.number,
  section: PropTypes.object,
  errorsTags: PropTypes.object,
  setErrorsTags: PropTypes.func,
};

export default Tags;
